import { Component, OnInit, computed, input, model } from '@angular/core';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ModalsService } from '@core/services/modals/modals.service';
import { Router } from '@angular/router';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { FidelityType } from '@shared/modals/kiosk-fidelity-registration/fidelity-type.enum';
import { ZerosixRegistrationStep } from './zerosix-registration-step.enum';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { take } from 'rxjs';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { toSignal } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Component({
  selector: 'app-zerosix-registration-modal',
  templateUrl: './zerosix-registration-modal.component.html',
})
export class ZerosixRegistrationModalComponent extends DestroyerBase implements OnInit {
  static handle = 'zerosix-registration-modal';

  phone: string = '';
  readonly goToURLOnClose = input<string>();
  readonly goToKioskRegister = input<boolean | undefined>(false);
  registrationStep = ZerosixRegistrationStep;
  readonly activeStep = model(this.registrationStep.ZEROSIX_STEP_PHONE);

  readonly actionFontColor = input<IContentBuilderFieldColor | string>();

  readonly actionBackgroundColor = input<IContentBuilderFieldColor | string>();

  readonly actionFieldColor = computed<ContentBuilderFieldColors | undefined>(() => {
    const font = this.actionFontColor();
    const background = this.actionBackgroundColor();
    if (!font && !background) {
      return undefined;
    }
    return {
      font: this.getFormattedFontColor(font),
      background: this.getFormattedBackgroundColor(background),
    };
  });

  protected readonly marketplaceLocal = toSignal(
    this.marketplaceStoreSelector.selectMarketplaceCountry,
    { initialValue: 'fr' },
  );

  public readonly FidelityType = FidelityType;

  constructor(
    private readonly modalsService: ModalsService,
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly zerosixService: ZerosixService,
    private readonly marketplaceStoreSelector: MarketplaceStoreSelector,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
  }

  private getFormattedFontColor(font: string | IContentBuilderFieldColor | undefined) {
    if (typeof font === 'string') {
      return font.startsWith('#')
        ? {
            type: ColorFieldTypesValues.Hex,
            value: font,
          }
        : {
            type: ColorFieldTypesValues.Palette,
            value: font,
          };
    }
    return font;
  }

  private getFormattedBackgroundColor(background: string | IContentBuilderFieldColor | undefined) {
    if (typeof background === 'string') {
      return background.startsWith('#')
        ? {
            type: ColorFieldTypesValues.Hex,
            value: background,
          }
        : {
            type: ColorFieldTypesValues.Palette,
            value: background,
          };
    }
    return background;
  }

  ngOnInit() {
    if (this.goToKioskRegister()) {
      this.onGoToRegisterStep();
    }
  }

  onClose(): void {
    this.modalsService.close(ZerosixRegistrationModalComponent.handle);
  }

  onComplete(): void {
    this.onClose();
    const goToURLOnClose = this.goToURLOnClose();
    if (goToURLOnClose) {
      this.analyticsService.log({
        level: 'debug',
        label: '#DOOD-1250 Navigate',
        message: {
          from: 'ZerosixRegistrationModalComponent Ligne°108',
          target: [
            goToURLOnClose ? this.routerHelper.translateRoute(goToURLOnClose) : goToURLOnClose,
          ],
          currentLocation: window.location.pathname,
        },
      });
      this.router.navigate([
        goToURLOnClose ? this.routerHelper.translateRoute(goToURLOnClose) : goToURLOnClose,
      ]);
    }
  }

  onGoToOtpStep(): void {
    this.zerosixService
      .isZerosixOtpIdentificationRequired$()
      .pipe(take(1))
      .subscribe(isOtpRequired => {
        if (isOtpRequired) {
          this.activeStep.set(this.registrationStep.ZEROSIX_STEP_OTP);
        } else {
          this.onGoToConfirmationStep();
        }
      });
  }

  setPhone(phone: string): void {
    this.phone = phone;
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_ACCOUNT_NOT_FOUND);
  }

  onGoToAccountNotFoundStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_ACCOUNT_NOT_FOUND);
  }

  onGoToPhoneStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_PHONE);
  }

  onGoToAuthenticationStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_AUTHENTICATION);
  }

  onGoToConfirmationStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_CONFIRMATION);
  }

  onGoToRegisterStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_REGISTER);
  }

  onGoToKioskRegisterConfirmationStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_REGISTER_CONFIRMATION);
  }

  onGoToKioskRegistrationSuccessStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_REGISTRATION_SUCCESS);
  }

  onGoToKioskAccountNotFoundStep() {
    this.activeStep.set(this.registrationStep.ZEROSIX_STEP_KIOSK_ACCOUNT_NOT_FOUND);
  }
}
